/**
 * iter 50 — Google Maps loader hook.
 *
 * Injects the Google Maps JS SDK with the Places library exactly once per
 * page load. Resolves a Promise so consumers can `await` readiness before
 * touching `window.google.maps`. Re-using the same script tag if it already
 * exists (component re-mounts won't double-load).
 */
import { useEffect, useState } from "react";

const SCRIPT_ID = "lb-google-maps-sdk";
let _loadingPromise = null;

export function loadGoogleMaps() {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  // Drawing is nice-to-have (Google may deprecate in newer SDK versions).
  // Only require `places` for the loader to be considered ready — the
  // command-center will feature-detect drawing before using it.
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (_loadingPromise) return _loadingPromise;

  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) {
    return Promise.reject(new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not configured"));
  }

  _loadingPromise = new Promise((resolve, reject) => {
    // Re-attach to a stale script if one is already in flight.
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.async = true;
    s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places,drawing,geometry&v=weekly`;
    s.onload = () => resolve(window.google);
    s.onerror = (e) => {
      _loadingPromise = null;   // allow retries
      reject(e);
    };
    document.head.appendChild(s);
  });
  return _loadingPromise;
}

export function useGoogleMaps() {
  const [ready, setReady] = useState(!!(typeof window !== "undefined"
    && window.google?.maps?.places));
  const [error, setError] = useState(null);
  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    loadGoogleMaps()
      .then(() => { if (!cancelled) setReady(true); })
      .catch((e) => { if (!cancelled) setError(e?.message || String(e)); });
    return () => { cancelled = true; };
  }, [ready]);
  return { ready, error };
}
